import { useState } from 'react';
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Circle, Leaf, MapPin, AlertTriangle, Droplets, Home, Eye, Tent } from 'lucide-react';

const CATEGORIES = [
  { value: 'mushroom', label: 'キノコ', color: '#d97706', icon: Circle },
  { value: 'wild_plant', label: '山菜', color: '#16a34a', icon: Leaf },
  { value: 'landmark', label: 'ランドマーク', color: '#f97316', icon: MapPin },
  { value: 'danger', label: '危険箇所', color: '#ef4444', icon: AlertTriangle },
  { value: 'water', label: '水場', color: '#3b82f6', icon: Droplets },
  { value: 'shelter', label: '避難小屋', color: '#8b5cf6', icon: Home },
  { value: 'viewpoint', label: '展望地', color: '#ec4899', icon: Eye },
  { value: 'campsite', label: 'テント場', color: '#059669', icon: Tent },
];

export default function WaypointDrawer({ open, onClose, position, onSave }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('landmark');
  const [tags, setTags] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onSave({
        lat: position?.lat,
        lng: position?.lng,
        name: name.trim(),
        description: description.trim(),
        category,
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
      });
      setName('');
      setDescription('');
      setCategory('landmark');
      setTags('');
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={(o) => !o && onClose()}>
      <DrawerContent className="glass-panel border-white/10 max-h-[85vh]" data-testid="waypoint-drawer">
        <DrawerHeader className="text-left">
          <DrawerTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase">
            ウェイポイント追加
          </DrawerTitle>
          <DrawerDescription>
            {position ? `${position.lat.toFixed(5)}, ${position.lng.toFixed(5)}` : ''}
          </DrawerDescription>
        </DrawerHeader>

        <div className="px-4 space-y-4 overflow-y-auto">
          {/* Category selector */}
          <div>
            <label className="text-xs tracking-widest uppercase text-muted-foreground mb-2 block">カテゴリ</label>
            <div className="grid grid-cols-4 gap-2" data-testid="category-selector">
              {CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                const isSelected = category === cat.value;
                return (
                  <button
                    key={cat.value}
                    onClick={() => setCategory(cat.value)}
                    className={`flex flex-col items-center gap-1.5 p-2.5 rounded-xl transition-all active:scale-95 ${
                      isSelected
                        ? 'bg-white/10 ring-1 ring-white/20'
                        : 'bg-white/5 hover:bg-white/8'
                    }`}
                    data-testid={`category-${cat.value}`}
                  >
                    <Icon size={18} style={{ color: cat.color }} strokeWidth={1.5} />
                    <span className="text-[10px] tracking-wider text-zinc-300">{cat.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="text-xs tracking-widest uppercase text-muted-foreground mb-1.5 block">名前</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例: マツタケのポイント"
              className="bg-white/5 border-white/10 h-11"
              data-testid="waypoint-name-input"
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-xs tracking-widest uppercase text-muted-foreground mb-1.5 block">メモ</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="発見した情報やメモを残す..."
              className="bg-white/5 border-white/10 min-h-[80px] resize-none"
              data-testid="waypoint-description-input"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="text-xs tracking-widest uppercase text-muted-foreground mb-1.5 block">タグ (カンマ区切り)</label>
            <Input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="例: 秋, 標高1000m"
              className="bg-white/5 border-white/10 h-11"
              data-testid="waypoint-tags-input"
            />
          </div>
        </div>

        <DrawerFooter>
          <Button
            onClick={handleSave}
            disabled={!name.trim() || saving}
            className="w-full h-12 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold tracking-wide"
            data-testid="waypoint-save-btn"
          >
            {saving ? '保存中...' : '保存'}
          </Button>
          <Button
            variant="ghost"
            onClick={onClose}
            className="w-full rounded-full text-zinc-400"
            data-testid="waypoint-cancel-btn"
          >
            キャンセル
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

export { CATEGORIES };
